import { useEffect, useMemo, useState } from "react";
import {
  FormProps,
  FormTypeProps,
} from "../../components/form/interface/formInterface";
import AdminAPI from "../../api/admin";
import DivStyle from "../../components/style/div.component";
import Table from "../../components/table/Table";
import Page from "../../components/table/paging";
import ButtonStyle from "../../components/style/button.component";
import { useSetRecoilState } from "recoil";
import { loadingState } from "../../recoil/loading.atom";
import { IEventSchedule } from "./interface/interface";
import SmallForm from "../../components/form/small_form";
import SearchEventAttendee from "./component/SearchEventAttendee";
import SearchFranchisePub from "./component/SearchPub";
import InputFormStyle from "../../components/style/input_form.component";
import Icon from "@mdi/react";
import { mdiMagnify } from "@mdi/js";
import { EventAttendeeType } from "../../api/admin/event-schedule";
import FormCheckInput from "../../components/form/form_check";

const EventSchedule = () => {
  const [dataList, setDataList] = useState<IEventSchedule[]>([]);
  const currentDate = new Date();
  const monthList = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const yearList = [2025, 2026, 2027];
  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  const [year, setYear] = useState(currentDate.getFullYear());
  const [isAsc, setIsAsc] = useState<boolean>(false);
  const [isAll, setIsAll] = useState<boolean>(false);
  const [id, setId] = useState(0);
  const [pubId, setPubId] = useState(0);
  const [pubName, setPubName] = useState("");
  const [attendeeId, setAttendeeId] = useState(0);
  const [attendeeName, setAttendeeName] = useState("");
  const [attendeeType, setAttendeeType] = useState(EventAttendeeType.YOUTUBER);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const setIsLoading = useSetRecoilState(loadingState);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [ord, setOrd] = useState(0);
  const [mode, setMode] = useState("I");
  const [isPubSearch, setIsPubSearch] = useState(false);
  const [isAttendeeSearch, setIsAttendeeSearch] = useState(false);
  const [search, setSearch] = useState("");
  const [link, setLink] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [isTextHidden, setIsTextHidden] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const formData: FormProps = useMemo(() => {
    const base: FormTypeProps[] = [
      {
        value: title,
        setValue: setTitle,
        label: "제목",
        width: "100%",
        type: "text",
      },
      {
        value: date,
        setValue: setDate,
        label: "날짜",
        width: "80%",
        type: "date-only",
      },
      {
        value: pubName,
        setValue: setPubName,
        label: "펍 이름",
        width: "100%",
        isOpen: isPubSearch,
        setIsOpen: setIsPubSearch,
        type: "search-group",
      },
      {
        value: attendeeName,
        setValue: setAttendeeName,
        label: "게스트",
        width: "100%",
        isOpen: isAttendeeSearch,
        setIsOpen: setIsAttendeeSearch,
        type: "search-group",
      },
      {
        value: link,
        setValue: setLink,
        label: "이동 링크",
        width: "100%",
        type: "text",
      },
      {
        value: ord,
        setValue: setOrd,
        label: "순서",
        width: "100%",
        type: "number",
      },
    ];

    if (attendeeType === EventAttendeeType.AMBASSADOR) {
      base.push({
        value: isTextHidden,
        setValue: setIsTextHidden,
        label: "텍스트 숨기기",
        type: "check",
        width: "100%",
      });
      base.push({
        value: selectedFile,
        setValue: setSelectedFile,
        label: "이미지",
        type: "file",
        width: "100%",
      });
      base.push({
        value: imageUrl,
        setValue: setImageUrl,
        label: "이미지",
        type: "image",
        width: "50%",
      });
    }

    return {
      title: "상세 정보",
      form: base,
    };
  }, [
    title,
    date,
    pubName,
    isPubSearch,
    attendeeName,
    isAttendeeSearch,
    link,
    ord,
    attendeeType,
    isTextHidden,
    selectedFile,
    imageUrl,
  ]);

  const headers = [
    {
      text: "id",
      align: "start",
      sortable: false,
      value: "id",
    },
    {
      text: "순서",
      align: "start",
      sortable: false,
      value: "ord",
    },
    {
      text: "날짜",
      align: "start",
      sortable: false,
      value: "date",
    },
    {
      text: "타이틀",
      align: "start",
      sortable: false,
      value: "title",
    },
    {
      text: "게스트",
      align: "center",
      sortable: false,
      value: "attendeeName",
    },
    {
      text: "펍",
      align: "center",
      sortable: false,
      value: "pubName",
    },
  ];

  const addForm = () => {
    setMode("I");
    setId(0);
    setOrd(0);
    setTitle("");
    setDate("");
    setPubName("");
    setPubId(0);
    setAttendeeName("");
    setAttendeeId(0);
    setIsTextHidden(false);
    setImageUrl("");
    setSelectedFile(null);
  };

  function formatDate(dateString: string) {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0"); // 월은 0부터 시작하므로 1을 더합니다.
    const day = date.getDate().toString().padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  const add = async () => {
    const formData = new FormData();
    formData.append("date", formatDate(date));
    formData.append("title", title);
    formData.append("pubName", pubName);
    formData.append("pubId", pubId.toString());
    formData.append("attendeeId", attendeeId.toString());
    formData.append("link", link);
    formData.append("attendeeName", attendeeName);
    formData.append("ord", ord ? ord.toString() : "0");
    if (isTextHidden) {
      formData.append("isTextHidden", isTextHidden.toString());
    }
    if (selectedFile) {
      formData.append("file", selectedFile);
    }
    if (window.confirm("신규등록 하시겠습니까?")) {
      setIsLoading(true);
      try {
        const res = await AdminAPI.registerEventSchedule(formData);
        if (res) {
          alert("신규 등록 되었습니다.");
          getData();
          addForm();
          setIsLoading(false);
        }
      } catch (e) {
        alert(e);
        setIsLoading(false);
      }
    }
  };

  const remove = async () => {
    if (window.confirm("삭제 하시겠습니까?")) {
      setIsLoading(true);
      const res = await AdminAPI.removeEventSchedule(id);
      if (res && res.status === 200) {
        alert("삭제 되었습니다.");
        getData();
        addForm();
      }
      setIsLoading(false);
    }
  };

  const save = async () => {
    const formData = new FormData();
    formData.append("date", formatDate(date));
    formData.append("title", title);
    formData.append("pubName", pubName);
    formData.append("pubId", pubId.toString());
    formData.append("attendeeId", attendeeId.toString());
    formData.append("link", link);
    formData.append("attendeeName", attendeeName);
    formData.append("ord", ord ? ord.toString() : "0");
    formData.append("isTextHidden", isTextHidden.toString());
    if (selectedFile) {
      formData.append("file", selectedFile);
    }
    setIsLoading(true);
    try {
      const res = await AdminAPI.updateEventSchedule(id, formData);
      if (res) {
        alert("저장 되었습니다.");
        getData();
        addForm();
        setIsLoading(false);
      }
    } catch (e: any) {
      alert(e.message);
      setIsLoading(false);
    }
  };

  const setDetails = (item: IEventSchedule) => {
    setMode("U");
    setId(item.id);
    setTitle(item.title);
    const date = formatDate(item.date);
    setDate(date);
    setPubName(item.pubName);
    setPubId(item.pubId);
    setAttendeeName(item.attendeeName);
    setAttendeeId(item.attendeeId);
    setIsTextHidden(item.isTextHidden);
    setAttendeeType(item.attendeeType);
    setImageUrl(item.attendeeImageUrl);
    setOrd(item.ord);
    setSelectedFile(null);
  };

  const getData = async () => {
    let res = null;
    if (!isAll) {
      res = await AdminAPI.getEventSchedule(
        search,
        currentPage,
        10,
        isAsc,
        month,
        year
      );
    } else {
      res = await AdminAPI.getEventSchedule(search, currentPage, 10, isAsc);
    }
    if (res) {
      const updatedData: IEventSchedule[] = res.data.list.map(
        (item: IEventSchedule) => ({
          ...item,
        })
      );

      setTotalPages(Math.max(1, Math.ceil(res.data.total / 10)));
      setDataList(updatedData);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    getData();
  }, [search, month, year, isAsc, isAll]);

  useEffect(() => {
    getData();
  }, [currentPage]);

  return (
    <DivStyle
      style={{ overflow: "auto" }}
      backgroundColor="#121212"
      height="100vh"
      display="flex"
      flex_direction="row"
      marginTop="65px"
      setMobile={true}
    >
      <DivStyle
        width="66%"
        height="auto"
        display="flex"
        flex_direction="column"
        align_items="center"
        setMobileTable={true}
      >
        <h2 style={{ color: "white", width: "auto", marginTop: "40px" }}>
          이벤트 스케쥴 관리
        </h2>
        <DivStyle
          width="100%"
          display="flex"
          justify_content="flex-end"
          flex_direction="row"
          align_items="center"
          marginRight="100px"
          marginTop="20px"
          gap="20px"
        >
          <DivStyle
            width="50%"
            display="flex"
            flex_direction="row"
            align_items="center"
            gap="10px"
          >
            <DivStyle color="#fff">검색어: </DivStyle>
            <InputFormStyle
              width="80%"
              onChange={(e) => {
                setSearch(e.target.value);
              }}
            ></InputFormStyle>
            <Icon path={mdiMagnify} size={1} style={{ color: "white" }} />
          </DivStyle>
          <DivStyle
            display="flex"
            flex_direction="row"
            align_items="flex-start"
            gap="10px"
          >
            <DivStyle color="#fff">전체: </DivStyle>
            <FormCheckInput
              label=""
              value={isAll}
              setValue={setIsAll}
              width="30%"
            />
          </DivStyle>
          {!isAll ? (
            <DivStyle
              display="flex"
              flex_direction="row"
              align_items="center"
              gap="10px"
            >
              <select
                value={year}
                onChange={(e) => setYear(+e.target.value)}
                style={{
                  backgroundColor: "#2C2C2C",
                  color: "white",
                  border: "1px solid #444",
                  borderRadius: "5px",
                  padding: "5px 10px",
                  fontSize: "14px",
                }}
              >
                {yearList.map((item, index) => (
                  <option value={item} key={index}>
                    {item}년
                  </option>
                ))}
              </select>
              <select
                value={month}
                onChange={(e) => setMonth(+e.target.value)}
                style={{
                  backgroundColor: "#2C2C2C",
                  color: "white",
                  border: "1px solid #444",
                  borderRadius: "5px",
                  padding: "5px 10px",
                  fontSize: "14px",
                }}
              >
                {monthList.map((item, index) => (
                  <option value={item} key={index}>
                    {item}월
                  </option>
                ))}
              </select>
            </DivStyle>
          ) : (
            <></>
          )}
          <DivStyle
            display="flex"
            flex_direction="row"
            align_items="flex-start"
            gap="10px"
          >
            <DivStyle color="#fff">과거순: </DivStyle>
            <FormCheckInput
              label=""
              value={isAsc}
              setValue={setIsAsc}
              width="30%"
            />
          </DivStyle>
        </DivStyle>
        <DivStyle
          width="95%"
          display="flex"
          backgroundColor="#1E1E1E"
          flex_direction="column"
          borderRadius="10px"
          marginTop="30px"
          marginBottom="10px"
        >
          <Table
            headers={headers}
            currentPage={currentPage}
            dataList={dataList}
            setDetail={setDetails}
            hasPagination={true}
          />
          <Page
            currentPage={currentPage}
            totalPages={totalPages}
            dataList={dataList}
            setCurrentPage={setCurrentPage}
          />
        </DivStyle>
        <DivStyle
          marginBottom="50px"
          width="95%"
          display="flex"
          justify_content="flex-end"
        >
          <ButtonStyle
            border="none"
            backgroundColor="#1976D2"
            width="85px"
            height="40px"
            color="white"
            borderRadius="5px"
            onClick={() => addForm()}
          >
            신규 등록
          </ButtonStyle>
        </DivStyle>
      </DivStyle>
      <DivStyle
        width="33%"
        height="auto"
        display="flex"
        flex_direction="column"
        align_items="center"
        setMobileForm={true}
      >
        <h2 style={{ color: "white", width: "auto", marginTop: "40px" }}>폼</h2>
        <DivStyle
          width="95%"
          display="flex"
          backgroundColor="#1E1E1E"
          flex_direction="column"
          borderRadius="10px"
          marginTop="30px"
          marginBottom="60px"
        >
          <SmallForm
            form={formData.form}
            title={formData.title}
            add={add}
            save={save}
            remove={remove}
            mode={mode}
          />
        </DivStyle>
      </DivStyle>
      {isAttendeeSearch && (
        <SearchEventAttendee
          setAttendeeId={setAttendeeId}
          setAttendeeName={setAttendeeName}
          setAttendeeType={setAttendeeType}
          setIsOpen={setIsAttendeeSearch}
        />
      )}
      {isPubSearch && (
        <SearchFranchisePub
          setPubId={setPubId}
          setPubName={setPubName}
          setIsOpen={setIsPubSearch}
        />
      )}
    </DivStyle>
  );
};

export default EventSchedule;
